/**
 * Widget Mount / Unmount / Create API
 *
 * This module bridges the plain-JS world (embed script, host page) and
 * the React application.  It exposes two usage patterns:
 *
 * ── Pattern 1: Singleton (simple HTML sites) ─────────────────────────
 *   TaxCalculator.mount('#container', { country: 'ES' })
 *   TaxCalculator.unmount()
 *
 * ── Pattern 2: Factory (React / SPA sites) ───────────────────────────
 *   const calc = TaxCalculator.create({
 *     parentEl: document.getElementById('my-div'),
 *     country: 'ES',
 *     onComplete: (summary) => { ... }
 *   });
 *   calc.destroy();
 *
 * Pattern 2 is better for SPAs because the host controls the lifecycle
 * and can create/destroy widgets on any route or step.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import WidgetApp from './WidgetApp';
import {
  WidgetConfig,
  parseConfigFromElement,
  mergeConfig,
} from './types';
import { setApiBaseUrl } from '../shared/apiClient';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function resolveElement(
  target?: HTMLElement | string | null,
): HTMLElement | null {
  if (!target) return document.getElementById('tax-calculator');
  if (typeof target === 'string') return document.querySelector<HTMLElement>(target);
  return target;
}

function buildConfig(
  el: HTMLElement,
  overrides?: Partial<WidgetConfig> | CreateWidgetOptions,
): WidgetConfig {
  const opts = overrides || {};
  const partial: Partial<WidgetConfig> = {};
  if ('country' in opts && opts.country !== undefined) partial.country = opts.country;
  if ('locale' in opts && opts.locale !== undefined) partial.locale = opts.locale;
  if ('taxYear' in opts && opts.taxYear !== undefined) partial.taxYear = opts.taxYear;
  if ('region' in opts && opts.region !== undefined) partial.region = opts.region;
  if ('theme' in opts && opts.theme !== undefined) partial.theme = opts.theme;
  if ('apiBaseUrl' in opts && opts.apiBaseUrl !== undefined) partial.apiBaseUrl = opts.apiBaseUrl;
  if ('onComplete' in opts && opts.onComplete !== undefined) partial.onComplete = opts.onComplete;

  const config = mergeConfig(parseConfigFromElement(el), partial);

  if (config.apiBaseUrl) {
    setApiBaseUrl(config.apiBaseUrl);
  }
  return config;
}

function renderInto(el: HTMLElement, config: WidgetConfig): ReactDOM.Root {
  const root = ReactDOM.createRoot(el);
  root.render(
    <React.StrictMode>
      <WidgetApp config={config} />
    </React.StrictMode>,
  );
  return root;
}

// ---------------------------------------------------------------------------
// Pattern 1 — Singleton mount / unmount (backward-compatible)
// ---------------------------------------------------------------------------

let singletonRoot: ReactDOM.Root | null = null;

export function mountWidget(
  container?: HTMLElement | string | null,
  overrides?: Partial<WidgetConfig>,
): void {
  if (singletonRoot) {
    console.warn('[TaxCalculator] Widget is already mounted. Call unmount() first.');
    return;
  }

  const el = resolveElement(container);
  if (!el) {
    console.error('[TaxCalculator] Mount target not found.');
    return;
  }

  const config = buildConfig(el, overrides);
  singletonRoot = renderInto(el, config);
}

export function unmountWidget(): void {
  if (singletonRoot) {
    singletonRoot.unmount();
    singletonRoot = null;
  }
}

// ---------------------------------------------------------------------------
// Pattern 2 — Factory: create() returns an instance with destroy()
// ---------------------------------------------------------------------------

export interface CreateWidgetOptions {
  /** DOM element or CSS selector to render into. Defaults to #tax-calculator. */
  parentEl?: HTMLElement | string;
  country?: string | null;
  locale?: string;
  taxYear?: number;
  region?: string;
  theme?: string;
  apiBaseUrl?: string;
  onComplete?: (summary: unknown) => void;
  /** Pass-through values (e.g. state, income) forwarded via widget config extras. */
  [key: string]: unknown;
}

export interface WidgetInstance {
  destroy: () => void;
}

export function createWidget(options: CreateWidgetOptions = {}): WidgetInstance {
  const el = resolveElement(options.parentEl || null);
  if (!el) {
    throw new Error('[TaxCalculator] parentEl not found. Pass a DOM element or CSS selector.');
  }

  const config = buildConfig(el, options);
  const root = renderInto(el, config);

  return {
    destroy() {
      root.unmount();
    },
  };
}

// ---------------------------------------------------------------------------
// Global namespace
// ---------------------------------------------------------------------------

export interface TaxCalculatorGlobal {
  mount: typeof mountWidget;
  unmount: typeof unmountWidget;
  create: typeof createWidget;
  version: string;
}

declare global {
  interface Window {
    TaxCalculator?: TaxCalculatorGlobal;
  }
}

export function registerGlobalApi(): void {
  if (window.TaxCalculator) return;
  window.TaxCalculator = {
    mount: mountWidget,
    unmount: unmountWidget,
    create: createWidget,
    version: '1.0.0',
  };
}
